import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, BarChart3, Settings, LogOut,
  Menu, X, ChevronDown, Bell, Search, Shield, Crown, CreditCard,
  Globe, Activity, HelpCircle, MessageSquare, Megaphone, Flag,
  Key, FileText, ScrollText, UserCog, Eye, EyeOff, Inbox, Mail, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { recaptchaToken } from '@/lib/recaptcha';
import GoogleSignInButton from '@/components/GoogleSignInButton';

import { API } from '@/config/api';
import { CA_BASE } from '@/utils/getBasePath';
const ca = (route = '') => route ? `${CA_BASE}/${route}` : (CA_BASE || '/');

// Company logo - using Chain29 logo
import { COMPANY_LOGO } from '@/config/brandAssets';

// Navigation items for Company Admin - Full V1 Feature Set
const navItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard, path: ca() },
  { id: 'restaurants', label: 'Restaurants', icon: Building2, path: ca('restaurants') },
  { id: 'onboarding', label: 'Onboarding Inbox', icon: Inbox, path: ca('onboarding') },
  { id: 'contact-messages', label: 'Contact Messages', icon: Mail, path: ca('support/contact-messages') },
  { id: 'users', label: 'Users & Accounts', icon: Users, path: ca('users') },
  { id: 'tickets', label: 'Support Tickets', icon: MessageSquare, path: ca('tickets') },
  { id: 'announcements', label: 'Announcements', icon: Megaphone, path: ca('announcements') },
  { id: 'feature-flags', label: 'Feature Flags', icon: Flag, path: ca('feature-flags') },
  { id: 'settings', label: 'Platform Settings', icon: Settings, path: ca('settings') },
  { id: 'api-keys', label: 'API Keys', icon: Key, path: ca('integration-keys') },
  { id: 'subscriptions', label: 'Subscriptions', icon: CreditCard, path: ca('subscriptions') },
  { id: 'reports', label: 'Reports', icon: FileText, path: ca('reports') },
  { id: 'audit-logs', label: 'Audit Logs', icon: ScrollText, path: ca('audit-logs') },
  { id: 'rbac', label: 'Roles & Permissions', icon: UserCog, path: ca('rbac') },
  { id: 'transactions', label: 'All Transactions', icon: Globe, path: ca('transactions') },
  { id: 'analytics', label: 'Analytics', icon: BarChart3, path: ca('analytics') },
  { id: 'notification-analytics', label: 'Notification Analytics', icon: Bell, path: ca('notification-analytics') },
  { id: 'auto-scaling', label: 'Auto-Scaling', icon: Zap, path: ca('auto-scaling') },
  { id: 'service-mesh', label: 'Service Mesh', icon: Activity, path: ca('service-mesh') },
];

export default function CompanyAdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('company_admin_token'));
  const [loading, setLoading] = useState(true);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const fetchAdminProfile = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-admin/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmin(response.data);
    } catch (error) {
      console.error('Profile fetch error:', error);
      if (error.response?.status === 401 || error.response?.status === 404) {
        localStorage.removeItem('company_admin_token');
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchAdminProfile();
    } else {
      setLoading(false);
    }
  }, [token, fetchAdminProfile]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoggingIn(true);
    try {
      // invisible, score-based reCAPTCHA (see lib/recaptcha.js)
      const recaptcha_token = await recaptchaToken('LOGIN');
      const response = await axios.post(`${API}/company-admin/login`, { ...loginForm, recaptcha_token });
      const accessToken = response.data.access_token;
      localStorage.setItem('company_admin_token', accessToken);
      setToken(accessToken);
      setAdmin(response.data.admin);
      toast.success('Welcome to Chain29 Company Admin!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    try {
      const response = await axios.post(`${API}/company-admin/google-login`, {
        credential: credential
      });
      const accessToken = response.data.access_token;
      localStorage.setItem('company_admin_token', accessToken);
      setToken(accessToken);
      setAdmin(response.data.admin);
      toast.success('Welcome to Chain29 Company Admin!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Google login failed. Admin account required.');
    }
  };

  const handleGoogleError = (error) => {
    toast.error('Google sign-in failed');
  };

  const handleLogout = () => {
    localStorage.removeItem('company_admin_token');
    setToken(null);
    setAdmin(null);
    toast.success('Logged out successfully');
  };

  const isActivePath = (path) => {
    const dashboardPath = ca();
    if (path === dashboardPath) {
      return location.pathname === dashboardPath || location.pathname === (CA_BASE || '/');
    }
    return location.pathname.startsWith(path);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#1a1f37]">
        <div className="flex flex-col items-center gap-4">
          <img src={COMPANY_LOGO} alt="Chain29" className="h-20 w-20 animate-pulse" />
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Login form - dark navy theme for Company Admin
  if (!token) {
    return (
      <div className="min-h-screen flex" data-testid="company-admin-login">
        {/* Left Side - Hero with Dark Navy */}
        <div className="hidden lg:flex lg:w-3/5 relative bg-[#1a1f37]">
          <img 
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&q=80"
            alt="Admin Dashboard"
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f37]/95 to-[#1a1f37]/80" />
          <div className="relative z-10 flex flex-col justify-end p-12 text-white">
            <div className="max-w-lg">
              <div className="flex items-center gap-2 mb-4">
                <Crown className="h-8 w-8 text-yellow-500" />
                <span className="text-yellow-500 font-semibold">SUPER ADMIN</span>
              </div>
              <h2 className="text-4xl font-bold mb-4">Company Administration</h2>
              <p className="text-lg text-gray-300">
                Manage all restaurants, users, transactions, and platform-wide settings from one central dashboard.
              </p>
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-2/5 flex flex-col min-h-screen bg-white">
          {/* Logo */}
          <div className="p-8">
            <div className="flex items-center gap-2">
              <img src={COMPANY_LOGO} alt="Chain29" className="h-8 w-8" />
              <span className="text-xl font-bold text-[#1a1f37]">asterpos</span>
              <Badge className="bg-yellow-500 text-white text-[10px] ml-1">ADMIN</Badge>
            </div>
          </div>
          
          {/* Form Container */}
          <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16">
            <div className="max-w-sm w-full mx-auto">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Company Admin</h1>
              <p className="text-gray-500 mb-6">Sign in to access the super admin portal</p>
              
              {/* Google Sign In */}
              <div className="mb-6">
                <GoogleSignInButton 
                  onSuccess={handleGoogleSuccess}
                  onError={handleGoogleError}
                  showOneTap={false}
                />
              </div>
              
              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">Or continue with email</span>
                </div>
              </div>
              
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Email</label>
                  <Input
                    type="email"
                    placeholder="admin@asterpos.com"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    required
                    data-testid="company-login-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-600 mb-2">Password</label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={loginForm.password}
                      onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                      className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 pr-10"
                      required
                      data-testid="company-login-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                
                <Button 
                  type="submit" 
                  disabled={isLoggingIn} 
                  className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                  data-testid="company-login-submit"
                >
                  {isLoggingIn ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
              
              {/* Security Badge */}
              <div className="flex items-center justify-center gap-2 text-xs text-gray-400 mt-6">
                <Shield className="h-3 w-3" />
                <span>Secured Access - Authorized Personnel Only</span>
              </div>
            </div>
          </div>
          
          {/* Footer */}
          <div className="p-8 text-center text-xs text-gray-400 border-t">
            <p>Chain29 Platform Administration</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex" data-testid="company-admin-dashboard">
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Sidebar - dark navy */}
      <aside className={cn(
        "fixed left-0 top-0 z-40 h-screen bg-[#1a1f37] transition-all duration-300",
        "lg:translate-x-0",
        sidebarOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0 lg:w-64"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center px-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img src={COMPANY_LOGO} alt="Chain29" className="h-10 w-10" />
              <Crown className="absolute -top-1 -right-1 h-4 w-4 text-yellow-500" />
            </div>
            <div>
              <span className="font-bold text-lg text-white">Chain29</span>
              <p className="text-[10px] text-yellow-500 -mt-1">COMPANY ADMIN</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100vh-10rem)]">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = isActivePath(item.path);
            
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate(item.path);
                  if (window.innerWidth < 1024) {
                    setSidebarOpen(false);
                  }
                }}
                className={cn(
                  "w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all",
                  isActive
                    ? "bg-blue-600 text-white"
                    : "text-gray-300 hover:bg-white/10 hover:text-white"
                )}
                data-testid={`company-nav-${item.id}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Help & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          <div className="space-y-1">
            <button className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-gray-300 hover:bg-white/10 hover:text-white">
              <HelpCircle className="h-5 w-5" />
              <span>Help & Support</span>
            </button>
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid="company-logout-btn"
            >
              <LogOut className="h-5 w-5" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64 transition-all duration-300">
        {/* Top Header */}
        <header className="sticky top-0 z-20 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-4">
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden"
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Search */}
            <div className="relative hidden md:block w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search restaurants, users..."
                className="pl-10 bg-gray-50 border-gray-200 h-10"
              />
            </div>
          </div>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* System Status */}
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-green-50 rounded-full">
              <Activity className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-600 font-medium">System Online</span>
            </div>

            {/* Notifications */}
            <Button variant="ghost" size="icon" className="text-gray-500 hover:text-gray-700">
              <Bell className="h-5 w-5" />
            </Button>

            {/* User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-3 hover:bg-gray-100 rounded-lg">
                  <div className="h-9 w-9 rounded-full bg-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                    {admin?.full_name?.charAt(0) || 'A'}
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-medium">{admin?.full_name || 'Admin'}</p>
                  <p className="text-xs text-gray-500 font-normal">{admin?.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(ca('settings'))}>
                  <Settings className="h-4 w-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Outlet context={{ token, admin }} />
        </main>
      </div>
    </div>
  );
}
